import React, { Profiler, ReactElement, useState } from 'react'
import getLogger from 'utils/log/log'

import styles from './DevView.module.less'

const log = getLogger('DevView')

// Check if React Compiler is active by detecting compiler-specific runtime behavior
// The compiler transforms components in a way that can be detected
function isReactCompilerActive(): boolean {
  // React 19 compiler adds optimizations that we can detect
  // One way is to check if the __REACT_COMPILER_RUNTIME global exists (in dev mode)
  // Another is to check React version and compiler plugin presence
  const hasCompilerRuntime = '__REACT_COMPILER_RUNTIME' in globalThis
  const isReact19 = React.version.startsWith('19')
  return hasCompilerRuntime || isReact19
}

// Test component: Without React 19 compiler, this would re-render on every parent state change
// With React 19 compiler, it should automatically be memoized
function ExpensiveChild({ name, count }: { name: string; count: number }) {
  log.debugging(`ExpensiveChild rendered with name="${name}", count=${count}`)

  // Simulate expensive computation
  const expensiveValue = Array.from({ length: 1000 }, (_, i) => i * count).reduce((a, b) => a + b, 0)

  return (
    <div className="p-2.5 border border-gray-300 my-2.5">
      <p>Name: {name}</p>
      <p>Count prop: {count}</p>
      <p>Expensive computed value: {expensiveValue}</p>
    </div>
  )
}

// Another child that doesn't receive changing props
function StaticChild() {
  log.debugging('StaticChild rendered - should NOT re-render when parent state changes')

  return (
    <div className="p-2.5 border border-green-500 my-2.5">
      <p>I'm a static child with no props</p>
      <p>React 19 compiler should prevent unnecessary re-renders of me!</p>
    </div>
  )
}

// Test component for callback memoization
function ButtonChild({ onClick, label }: { onClick: () => void; label: string }) {
  log.debugging(`ButtonChild rendered with label="${label}"`)

  return (
    <button type="button" onClick={onClick} className="m-1.5 p-2.5 bg-blue-500 text-white rounded hover:bg-blue-600">
      {label}
    </button>
  )
}

function React19CompilerTest() {
  const [count, setCount] = useState(0)
  const [unrelatedState, setUnrelatedState] = useState(0)

  // Without React 19 compiler, this callback would need useCallback to prevent child re-renders
  const handleClick = () => {
    log.debugging('Button clicked!')
  }

  return (
    <div className="p-5">
      <h2>React 19 Compiler Auto-Memoization Test</h2>
      <p className="text-gray-500 mb-5">
        Open the console to see which components re-render. With React 19 compiler, only components whose props actually
        change should re-render.
      </p>

      <div className="mb-5">
        <button
          type="button"
          onClick={() => setCount((c) => c + 1)}
          className="mr-2.5 p-2.5 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Increment Count: {count}
        </button>
        <button
          type="button"
          onClick={() => setUnrelatedState((s) => s + 1)}
          className="p-2.5 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Change Unrelated State: {unrelatedState}
        </button>
      </div>

      <h3>Test Cases:</h3>

      {/* This should re-render when count changes */}
      <p>1. ExpensiveChild (receives count prop - should re-render on count change):</p>
      <ExpensiveChild name="Test" count={count} />

      {/* This should NOT re-render when any parent state changes */}
      <p>2. StaticChild (no props - should never re-render):</p>
      <StaticChild />

      {/* This tests callback memoization */}
      <p>3. ButtonChild (receives callback - should not re-render on unrelated state change):</p>
      <ButtonChild onClick={handleClick} label="Click me" />
    </div>
  )
}

export default function DevView(): ReactElement {
  const compilerActive = isReactCompilerActive()

  return (
    <div className={styles.DevView}>
      <h1>DEV</h1>

      <div className="mb-5 p-4 rounded border border-gray-300">
        <h2 className="text-lg font-bold mb-2">React 19 Compiler Status</h2>
        <p>
          <strong>React Version:</strong> {React.version}
        </p>
        <p>
          <strong>Compiler Active:</strong>{' '}
          <span className={compilerActive ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
            {compilerActive ? '✅ Yes' : '❌ No'}
          </span>
        </p>
        <p className="text-gray-500 text-sm mt-2">
          {compilerActive
            ? 'The React 19 compiler is active. Components are automatically memoized.'
            : 'The React 19 compiler may not be active. Check babel configuration.'}
        </p>
      </div>

      <Profiler
        id="react19-compiler-test"
        onRender={(id, phase, actualDuration) => {
          log.debugging(`${id}: ${actualDuration}ms to render ${phase}`)
        }}
      >
        <React19CompilerTest />
      </Profiler>
    </div>
  )
}
